import { FormControl, FormLabel } from '@chakra-ui/react';
import { useExternalGetDistinctOfficeNamesQuery } from '@client/graphql/__generated__/main-operations';
import { Select } from '@document/common/Select';
import { FC, useMemo } from 'react';
import { useOrganizationCompliancePage } from '~/apps/consumer/pages/Organizations/Compliance/OrganizationCompliancePageContext';

interface OfficeOption {
  label: string;
  value: string;
}

export const OfficeFilter: FC = () => {
  const { officeFilter, setOfficeFilter, organizationId } =
    useOrganizationCompliancePage();

  // Fetch distinct office names for the organization
  const { data: officeNamesData, loading } =
    useExternalGetDistinctOfficeNamesQuery({
      variables: {
        input: {
          organizationId: organizationId!,
        },
      },
      skip: !organizationId,
    });

  // Convert office names to options
  const officeOptions: OfficeOption[] = useMemo(() => {
    if (!officeNamesData?.externalGetDistinctOfficeNames) return [];

    return officeNamesData.externalGetDistinctOfficeNames.map((officeName) => ({
      label: officeName,
      value: officeName,
    }));
  }, [officeNamesData]);

  // Find the selected options
  const selectedOptions = officeFilter
    ? officeOptions.filter((option) => officeFilter.includes(option.value))
    : [];

  return (
    <FormControl>
      <FormLabel>Office</FormLabel>
      <Select
        isClearable
        isMulti
        isLoading={loading}
        options={officeOptions}
        placeholder="All offices"
        size="sm"
        styles={{
          container: (provided) => ({
            ...provided,
            minWidth: 150,
          }),
          menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
          }),
        }}
        value={selectedOptions}
        onChange={(options) =>
          setOfficeFilter(
            options && options.length > 0
              ? options.map((option) => option.value)
              : null
          )
        }
      />
    </FormControl>
  );
};
